import { NodeMetricsSchema } from '@/domain/metrics/contracts';
import type {
  DocumentationContext,
  DocumentationGraphExtension,
} from '@/domain/documentation/contracts';
import {
  DEFAULT_DOCUMENTATION_EXTENSION,
  DocumentationGraphExtensionSchema,
} from '@/domain/documentation/contracts';
import type { EditorGraph } from '@/features/funnel-editor/domain/editor-graph';
import { evaluateGraphMetrics } from '@/features/funnel-editor/metrics/evaluate-graph-metrics';
import { getNodeDefinition } from '@/features/funnel-editor/nodes/registry/node-registry';

export function readDocumentationExtension(graph: EditorGraph): DocumentationGraphExtension {
  const parsed = DocumentationGraphExtensionSchema.safeParse(graph.documentation);
  return parsed.success ? parsed.data : structuredClone(DEFAULT_DOCUMENTATION_EXTENSION);
}

export function buildDocumentationContext(
  graph: EditorGraph,
  funnel: { name: string; description?: string | null; ownerName?: string },
  optional?: {
    scenarioTarget?: DocumentationContext['scenarioTarget'];
    diagnosis?: DocumentationContext['diagnosis'];
    diagnosisCoverage?: number;
  },
): DocumentationContext {
  const metrics = evaluateGraphMetrics(graph);
  return {
    funnel,
    nodes: graph.nodes.map((node) => {
      const definition = getNodeDefinition(node.type ?? '');
      const nodeMetrics = metrics.propagation.nodeResults.get(node.id)!;
      const persisted = NodeMetricsSchema.safeParse(node.data.metrics);
      const goal = persisted.success ? (persisted.data.goal?.manualValue ?? undefined) : undefined;
      return {
        id: node.id,
        type: node.type ?? 'unknown',
        name: String(node.data.name ?? node.data.label ?? definition?.label ?? 'Untitled stage'),
        ...(typeof node.data.description === 'string' && node.data.description.trim()
          ? { description: node.data.description.trim() }
          : {}),
        ...(typeof node.data.responsible === 'string' && node.data.responsible.trim()
          ? { responsible: node.data.responsible.trim() }
          : {}),
        ...(typeof node.data.status === 'string' ? { status: node.data.status } : {}),
        category: definition?.category ?? String(node.data.category ?? 'documentation'),
        ...(typeof node.data.systemName === 'string' && node.data.systemName.trim()
          ? { systemName: node.data.systemName.trim() }
          : {}),
        metrics: nodeMetrics as unknown as Record<string, typeof nodeMetrics.entries>,
        ...(goal ? { goal } : {}),
        hasAttachments: Array.isArray(node.data.attachments) && node.data.attachments.length > 0,
      };
    }),
    edges: graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      ...(typeof edge.data?.label === 'string' && edge.data.label.trim()
        ? { label: edge.data.label.trim() }
        : {}),
      ...(typeof edge.data?.condition === 'string' && edge.data.condition.trim()
        ? { condition: edge.data.condition.trim() }
        : {}),
    })),
    funnelMetrics: metrics.funnel as unknown as DocumentationContext['funnelMetrics'],
    config: readDocumentationExtension(graph),
    ...optional,
  };
}
